import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import QRCode from 'react-qr-code';

const TableManager = ({ restaurant, onClose }) => {
  const [tables, setTables] = useState([]);
  const [newTableNum, setNewTableNum] = useState('');
  const [loading, setLoading] = useState(false);

  const isPro = restaurant.is_pro;

  // The General URL (No table number)
  const generalMenuUrl = `${window.location.origin}/menu/${restaurant.id}`;

  useEffect(() => {
    if (isPro) {
      const fetchTables = async () => {
        const { data } = await supabase
          .from('tables')
          .select('*')
          .eq('restaurant_id', restaurant.id)
          .order('table_number', { ascending: true });
        setTables(data || []);
      };
      fetchTables();
    }
  }, [isPro, restaurant.id]);

  const addTable = async (e) => {
    e.preventDefault();
    if (!newTableNum.trim()) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('tables')
      .insert([{ restaurant_id: restaurant.id, table_number: newTableNum }])
      .select()
      .single();

    if (error) alert(error.message);
    else {
      setTables([...tables, data]);
      setNewTableNum('');
    }
    setLoading(false);
  };

  const deleteTable = async (id) => {
    if (!window.confirm("Remove this table?")) return;
    await supabase.from('tables').delete().eq('id', id);
    setTables(tables.filter(t => t.id !== id));
  };

  // Robust Canvas-based download (replacing html-to-image for better reliability)
  const downloadQR = (tableNum) => {
    // 1. Find the SVG
    const svgId = `qr-svg-${tableNum}`;
    const svg = document.getElementById(svgId);

    if (!svg) {
      alert("QR Code not found. Please try again.");
      return;
    }

    // 2. Serialize SVG (Make it an image source)
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // 3. Create Canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const padding = 40;
      const textHeight = 60;
      const qrSize = img.width; // Should be around 300 based on props

      canvas.width = qrSize + (padding * 2);
      canvas.height = qrSize + (padding * 2) + textHeight;

      // 4. Draw Background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 5. Draw Border (Optional, mimics the CSS design)
      ctx.strokeStyle = "black";
      ctx.lineWidth = 10;
      ctx.strokeRect(0, 0, canvas.width, canvas.height);

      // 6. Draw QR
      ctx.drawImage(img, padding, padding);

      // 7. Draw Text
      ctx.font = "bold 40px sans-serif";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`TABLE ${tableNum}`, canvas.width / 2, canvas.height - (padding + (textHeight / 2)));

      // 8. Download
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `Table-${tableNum}-QR.png`;
      downloadLink.href = pngFile;
      downloadLink.click();

      // Cleanup
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };




  const downloadQRCanvas = (svgElement, labelText, fileName) => {
    if (!svgElement) return;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const padding = 40;
      const textHeight = 50;
      // Use natural size of SVG (likely 200 or 300)
      const qrSize = 300; // Force robust size

      canvas.width = qrSize + (padding * 2);
      canvas.height = qrSize + (padding * 2) + textHeight;

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Simple border
      ctx.strokeStyle = "rgba(0,0,0,0.1)";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);

      ctx.drawImage(img, padding, padding, qrSize, qrSize);

      ctx.font = "bold 24px sans-serif";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(labelText, canvas.width / 2, canvas.height - 30);

      const link = document.createElement("a");
      link.download = `${fileName}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };


  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-fade-in">

        {/* HEADER */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">QR Code Manager</h2>
            <p className="text-xs text-gray-500">Generate and print QR codes for your customers.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black font-bold text-2xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

            {/* === LEFT SIDE: GENERAL QR (FREE & PRO) === */}
            <div className="flex flex-col items-center">
              <div className="text-center mb-6">
                <span className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-xs mb-3 inline-block">
                  General Menu QR
                </span>
                <h3 className="text-2xl font-bold text-gray-900">Restaurant Wide</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Place this at the entrance or counter. <br />
                  Opens menu without a specific table number.
                </p>
              </div>

              {/* QR Display */}
              <div
                id="general-qr"
                className="bg-white p-6 rounded-2xl border-2 border-gray-900 shadow-xl mb-6"
              >
                <div className="flex flex-col items-center">
                  <QRCode value={generalMenuUrl} size={200} />
                  <p className="mt-4 font-bold text-gray-900 uppercase tracking-widest text-sm">SCAN FOR MENU</p>
                </div>
              </div>

              <button
                onClick={() => {
                  const svg = document.querySelector('#general-qr svg');
                  downloadQRCanvas(svg, "SCAN FOR MENU", "General-Menu-QR");
                }}
                className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black shadow-lg transition-transform hover:scale-105"
              >
                <span>⬇️</span> Download PNG
              </button>
            </div>


            {/* === RIGHT SIDE: TABLE SPECIFIC (PRO ONLY) === */}
            <div className="border-l border-gray-100 pl-0 md:pl-12 relative">

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900">Table Specific QRs</h3>
                <p className="text-gray-500 text-sm">Track which table ordered what.</p>
              </div>

              {/* IF NOT PRO - SHOW LOCK OVERLAY */}
              {!isPro ? (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex flex-col items-center justify-center text-center p-6 border-2 border-dashed border-gray-200 rounded-xl">
                  <div className="text-5xl mb-4">🔒</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Table Ordering</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-xs">
                    Upgrade to Pro to generate unique QR codes for every table and track live orders by seat.
                  </p>
                  <button className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-lg font-bold shadow-md hover:opacity-90">
                    Upgrade Now
                  </button>
                </div>
              ) : (
                /* PRO CONTENT */
                <>
                  {/* Add Table Form */}
                  <form onSubmit={addTable} className="flex gap-2 mb-6">
                    <input
                      type="text"
                      placeholder="Table No (e.g. 12)"
                      className="flex-1 p-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                      value={newTableNum}
                      onChange={(e) => setNewTableNum(e.target.value)}
                    />
                    <button disabled={loading} className="bg-blue-600 text-white px-6 rounded-xl font-bold hover:bg-blue-700">
                      {loading ? '...' : 'Add'}
                    </button>
                  </form>

                  {/* Tables List */}
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {tables.length === 0 && <p className="text-gray-400 text-sm italic">No tables added yet.</p>}

                    {tables.map(table => {
                      const tableUrl = `${generalMenuUrl}?table=${table.table_number}`;
                      // No div id needed for download anymore, just the SVG ID in the hidden section

                      return (
                        <div key={table.id} className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between group hover:bg-white hover:shadow-md transition-all">

                          <div className="flex items-center gap-4">
                            {/* Small QR Preview */}
                            <div className="bg-white p-2 rounded shadow-sm">
                              <QRCode value={tableUrl} size={40} />
                            </div>

                            <div>
                              <h4 className="font-bold text-gray-900">Table {table.table_number}</h4>
                              <p className="text-[10px] text-gray-400 font-mono">.../menu?table={table.table_number}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => downloadQR(table.table_number)}
                              className="text-gray-400 hover:text-blue-600 p-2 rounded hover:bg-blue-50 transition-colors"
                              title="Download QR"
                            >
                              ⬇️
                            </button>
                            <button
                              onClick={() => deleteTable(table.id)}
                              className="text-gray-400 hover:text-red-600 p-2 rounded hover:bg-red-50 transition-colors"
                              title="Delete Table"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* HIDDEN QR CODES SOURCE (Used for generating High-Res Canvas) */}
      {/* We utilize these ONLY to generate the SVGs that we then draw to canvas */}
      <div style={{ position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none', zIndex: -100 }}>
        {tables.map(table => (
          <div key={`qr-source-${table.id}`}>
            <QRCode
              id={`qr-svg-${table.table_number}`}
              value={`${generalMenuUrl}?table=${table.table_number}`}
              size={300}
            />
          </div>
        ))}
      </div>

    </div>
  );
};

export default TableManager;